import { useEffect, useRef, useState, type ReactNode } from 'react';
import { GENDERS, MARITAL_STATUSES, PERSON_STATUSES } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { Button, Field } from '@/components/ui';
import {
  deleteSavedSearch,
  listSavedSearches,
  saveSearch,
  type SavedSearch,
} from './saved-searches';

export interface PeopleFilterState {
  readonly status: (typeof PERSON_STATUSES)[number] | '';
  readonly gender: (typeof GENDERS)[number] | '';
  readonly maritalStatus: (typeof MARITAL_STATUSES)[number] | '';
  readonly birthDateFrom: string;
  readonly birthDateTo: string;
}

export const EMPTY_PEOPLE_FILTERS: PeopleFilterState = {
  status: '',
  gender: '',
  maritalStatus: '',
  birthDateFrom: '',
  birthDateTo: '',
};

export function activeFilterCount(filters: PeopleFilterState): number {
  return Object.values(filters).filter((value) => value !== '').length;
}

/**
 * The advanced-search filter panel, a right-side slide-over.
 *
 * A native `<dialog>` positioned via CSS rather than a hand-rolled overlay —
 * the same modal primitive every other dialog in the app uses, just
 * repositioned. Unlike the person-detail view, this has no deep-linking
 * requirement, so a dialog costs nothing here.
 *
 * Real, wired-up filters (Tipo de búsqueda / Género / Estado civil / Fecha
 * de nacimiento) sit alongside sections the reference screenshots show but
 * TABITA has no backend for yet (Grupos, Dirección, Foto, Actividades,
 * Seguimientos, date-range filters on created/updated/joined) — those
 * render as inert, "coming soon" rows so the shell matches the reference
 * honestly, without pretending they work.
 */
export function PeopleFilterDrawer({
  value,
  onApply,
  onClose,
}: {
  readonly value: PeopleFilterState;
  readonly onApply: (filters: PeopleFilterState) => void;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [draft, setDraft] = useState(value);
  const [savedSearches, setSavedSearches] = useState<readonly SavedSearch[]>(() =>
    listSavedSearches(),
  );

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  function apply(filters: PeopleFilterState): void {
    onApply(filters);
    onClose();
  }

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="people-drawer"
      aria-labelledby="people-drawer-title"
    >
      <div className="people-drawer-header">
        <h2 id="people-drawer-title">{t('people.filters.title')}</h2>
        <button
          type="button"
          className="people-drawer-close"
          onClick={onClose}
          aria-label={t('common.actions.close')}
        >
          ×
        </button>
      </div>

      <div className="people-drawer-body">
        <DrawerSection title={t('people.filters.status')}>
          <RadioGroup
            name="status"
            value={draft.status}
            options={PERSON_STATUSES.filter((status) => status !== 'ARCHIVED')}
            translate={(status) => t(`people.person.status.${status}`)}
            onChange={(status) => setDraft({ ...draft, status })}
          />
        </DrawerSection>

        <DrawerSection title={t('people.filters.gender')}>
          <RadioGroup
            name="gender"
            value={draft.gender}
            options={GENDERS}
            translate={(gender) => t(`people.person.gender.${gender}`)}
            onChange={(gender) => setDraft({ ...draft, gender })}
          />
        </DrawerSection>

        <DrawerSection title={t('people.filters.maritalStatus')}>
          <RadioGroup
            name="maritalStatus"
            value={draft.maritalStatus}
            options={MARITAL_STATUSES}
            translate={(status) => t(`people.person.maritalStatus.${status}`)}
            onChange={(maritalStatus) => setDraft({ ...draft, maritalStatus })}
          />
        </DrawerSection>

        <DrawerSection title={t('people.filters.birthDateRange')}>
          <div className="people-date-range">
            <Field
              label={t('people.filters.from')}
              name="birthDateFrom"
              type="date"
              value={draft.birthDateFrom}
              onChange={(e) => setDraft({ ...draft, birthDateFrom: e.target.value })}
            />
            <Field
              label={t('people.filters.to')}
              name="birthDateTo"
              type="date"
              value={draft.birthDateTo}
              onChange={(e) => setDraft({ ...draft, birthDateTo: e.target.value })}
            />
          </div>
        </DrawerSection>

        <DrawerSection title={t('people.filters.groups')} disabled />
        <DrawerSection title={t('people.filters.address')} disabled />
        <DrawerSection title={t('people.filters.photo')} disabled />
        <DrawerSection title={t('people.filters.activities')} disabled />
        <DrawerSection title={t('people.filters.followUps')} disabled />
        <DrawerSection title={t('people.filters.createdAtRange')} disabled />
        <DrawerSection title={t('people.filters.updatedAtRange')} disabled />
        <DrawerSection title={t('people.filters.joinedAtRange')} disabled />

        <DrawerSection title={t('people.filters.savedSearches.title')}>
          <SavedSearchList
            searches={savedSearches}
            onApply={(search) => apply(search.filters)}
            onDelete={(id) => {
              deleteSavedSearch(id);
              setSavedSearches(listSavedSearches());
            }}
          />
        </DrawerSection>
      </div>

      <div className="people-drawer-footer">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            const name = window.prompt(t('people.filters.savedSearches.namePrompt'));
            if (name?.trim()) {
              saveSearch(name.trim(), draft);
              setSavedSearches(listSavedSearches());
            }
          }}
        >
          {t('people.filters.savedSearches.save')}
        </Button>
        <div className="people-drawer-footer-actions">
          <Button type="button" variant="ghost" onClick={() => apply(EMPTY_PEOPLE_FILTERS)}>
            {t('people.filters.reset')}
          </Button>
          <Button type="button" variant="primary" onClick={() => apply(draft)}>
            {t('people.filters.apply')}
          </Button>
        </div>
      </div>
    </dialog>
  );
}

function DrawerSection({
  title,
  disabled = false,
  children,
}: {
  readonly title: string;
  readonly disabled?: boolean;
  readonly children?: ReactNode;
}) {
  const { t } = useI18n();

  if (disabled) {
    return (
      <div className="people-drawer-section" data-disabled>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {title}
          <span className="people-drawer-badge-soon">{t('people.filters.comingSoon')}</span>
        </span>
      </div>
    );
  }

  return (
    <details className="people-drawer-section">
      <summary>{title}</summary>
      <div className="people-drawer-section-body">{children}</div>
    </details>
  );
}

function RadioGroup<T extends string>({
  name,
  value,
  options,
  translate,
  onChange,
}: {
  readonly name: string;
  readonly value: T | '';
  readonly options: readonly T[];
  readonly translate: (value: T) => string;
  readonly onChange: (value: T | '') => void;
}) {
  const { t } = useI18n();
  return (
    <div className="people-radio-group" role="radiogroup" aria-label={name}>
      <label>
        <input type="radio" name={name} checked={value === ''} onChange={() => onChange('')} />
        <span>{t('people.filters.any')}</span>
      </label>
      {options.map((option) => (
        <label key={option}>
          <input
            type="radio"
            name={name}
            checked={value === option}
            onChange={() => onChange(option)}
          />
          <span>{translate(option)}</span>
        </label>
      ))}
    </div>
  );
}

function SavedSearchList({
  searches,
  onApply,
  onDelete,
}: {
  readonly searches: readonly SavedSearch[];
  readonly onApply: (search: SavedSearch) => void;
  readonly onDelete: (id: string) => void;
}) {
  const { t } = useI18n();

  if (searches.length === 0) {
    return <p className="people-drawer-badge-soon">{t('people.filters.savedSearches.empty')}</p>;
  }

  return (
    <div className="people-saved-searches">
      {searches.map((search) => (
        <div key={search.id} className="people-saved-search-row">
          <Button type="button" variant="ghost" onClick={() => onApply(search)}>
            {search.name}
          </Button>
          <Button
            type="button"
            variant="ghost"
            aria-label={t('common.actions.delete')}
            onClick={() => onDelete(search.id)}
          >
            ×
          </Button>
        </div>
      ))}
    </div>
  );
}
